import React, { Fragment, lazy, Suspense } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import AuthGuard from "src/components/AuthGuard";
import GuestGuard from "src/components/GuestGuard";
import LoadingScreen from "src/components/LoadingScreen";
import DashboardLayout from "src/layouts/DashboardLayout";
import MainLayout from "src/layouts/MainLayout";
import { AppProvider } from "./contexts/AppContext";
import account_routes from "./routes/account_routes";
import app_routes from "./routes/app_routes";
import approval_routes from "./routes/approval_routes";
import audit_routes from "./routes/audit_routes";
import cadaster_routes from "./routes/cadaster_routes";
import certificate_admin_routes from "./routes/certificate_admin_routes";
import classroom_routes from "./routes/classroom_routes";
import corporate_routes from "./routes/corporate_routes";
import dev_routes from "./routes/dev_routes";
import dinvoice_routes from "./routes/dinvoice_routes";
import environment_routes from "./routes/environment_routes";
import erp_routes from "./routes/erp_routes";
import eventads_routes from "./routes/eventads_routes";
import fire_department_routes from "./routes/fire_department_routes";
import informal_economy_routes from "./routes/informal_economy_routes";
import notify_routes from "./routes/notify_routes";
import planning_routes from "./routes/planning_routes";
import police_routes from "./routes/police_routes";
import poll_routes from "./routes/poll_routes";
import requests_routes from "./routes/requests_routes";
import service_routes from "./routes/service_routes";
import signature_routes from "./routes/signature_routes";
import socialresp_routes from "./routes/socialresp_routes";
import stamp_routes from "./routes/stamp_routes";
import stripe_routes from "./routes/stripe_routes";
import suppliers_routes from "./routes/suppliers_routes";
import taxes_routes from "./routes/taxes_routes";
import urbin_routes from "./routes/urbin_routes";
const ShopLayout = lazy(() => import("./views/arkadu/accounts/ShopLayout"));
const BankLayout = lazy(() => import("./views/arkadu/bank/BankLayout"));
const BlogIndex = lazy(() => import("./views/arkadu/blog/BlogIndex"));
const BlogLayout = lazy(() => import("./views/arkadu/blog/BlogLayout"));
const CadasterLayout = lazy(
  () => import("./views/arkadu/cadaster/CadasterLayout"),
);
const CorporateLayout = lazy(
  () => import("./views/arkadu/corporate/CorporateLayout"),
);
const CustomerLayout = lazy(
  () => import("./views/arkadu/customer/CustomerLayout"),
);
const DevLayout = lazy(() => import("./views/arkadu/dev/DevLayout"));
const EnvironmentLayout = lazy(
  () => import("./views/arkadu/environment/EnvironmentLayout"),
);
const FireDepartmentLayout = lazy(
  () => import("./views/arkadu/fireDepartment/FireDepartmentLayout"),
);
const EventadsLayout = lazy(
  () => import("./views/arkadu/eventads/EventadsLayout"),
);
const EventadsPublicPortal = lazy(
  () => import("./views/arkadu/eventads/EventadsPublicPortal"),
);
const GlobalLayout = lazy(() => import("./views/arkadu/global/GlobalLayout"));
const VehicleMotorLayout = lazy(
  () => import("./views/arkadu/motor/VehicleMotorLayout"),
);
const PlanningLayout = lazy(
  () => import("./views/arkadu/planning/PlanningLayout"),
);
const PollLayout = lazy(() => import("./views/arkadu/poll/PollLayout"));
const ServicesLayout = lazy(
  () => import("./views/arkadu/service/NetsServiceLayout"),
);
const StampLayout = lazy(() => import("./views/arkadu/stamp/StampLayout"));
const SuppliersLayout = lazy(
  () => import("./views/arkadu/suppliers/SuppliersLayout"),
);
const InformalEconomyLayout = lazy(
  () => import("./views/arkadu/taxes/informal_economy/InformalEconomyLayout"),
);
const TaxesLayout = lazy(() => import("./views/arkadu/taxes/TaxesLayout"));
const UrbinLayout = lazy(() => import("./views/arkadu/urbin/UrbinLayout"));
const UserLayout = lazy(() => import("./views/arkadu/user/UserLayout"));

const ensureArray = (value) => {
  if (value === undefined || value === null) {
    return [null];
  }
  return Array.isArray(value) ? value : [value];
};

const normalizePathValue = (path) => {
  if (path === null || path === undefined) {
    return null;
  }

  if (path === "") {
    return "";
  }

  return String(path);
};

const stripTrailingSlash = (path) => {
  if (!path || path === "/") {
    return "/";
  }
  return path.endsWith("/") ? path.slice(0, -1) : path;
};

const combinePath = (parentPath, routePath) => {
  const parent = normalizePathValue(parentPath);
  const child = normalizePathValue(routePath);

  if (child === null) {
    return parent;
  }

  if (child.startsWith("/")) {
    return stripTrailingSlash(child);
  }

  if (child === "") {
    if (!parent || parent === "") {
      return "/";
    }
    return stripTrailingSlash(parent);
  }

  if (child === "*") {
    if (!parent || parent === "") {
      return "*";
    }
    return `${stripTrailingSlash(parent)}/*`;
  }

  if (!parent || parent === "" || parent === "*") {
    return `/${child}`;
  }

  return `${stripTrailingSlash(parent)}/${child}`;
};

const fallbackPathFor = (parentPath) => {
  if (!parentPath || parentPath === "") {
    return "*";
  }
  if (parentPath === "*") {
    return "*";
  }
  return `${stripTrailingSlash(parentPath)}/*`;
};

const RouteComponentAdapter = ({ Component }) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const history = {
    push: (to) => navigate(to),
    replace: (to) => navigate(to, { replace: true }),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
  };

  return <Component history={history} location={location} match={{ params }} />;
};

const buildRouteEntries = (routes = [], parentPaths = [""], wrappers = []) => {
  const entries = [];

  routes.forEach((route) => {
    const Guard = route.guard || Fragment;
    const Layout = route.layout || Fragment;
    const currentWrappers = [...wrappers, { Guard, Layout }];
    const routePaths = ensureArray(route.path).map(normalizePathValue);

    const nextParentPaths = routePaths.flatMap((routePath) => {
      if (routePath === "*" && route.routes) {
        return parentPaths;
      }

      return parentPaths.map((parentPath) =>
        combinePath(parentPath, routePath),
      );
    });

    if (route.routes) {
      entries.push(
        ...buildRouteEntries(route.routes, nextParentPaths, currentWrappers),
      );
      return;
    }

    if (!route.component) {
      return;
    }

    const candidatePaths =
      routePaths[0] === null ? nextParentPaths : nextParentPaths;
    const resolvedPaths = candidatePaths.map((candidatePath) => {
      if (routePaths[0] === null) {
        return fallbackPathFor(candidatePath);
      }
      return candidatePath;
    });

    resolvedPaths.forEach((path) => {
      const baseElement = (
        <AppProvider>
          <RouteComponentAdapter Component={route.component} />
        </AppProvider>
      );

      const wrappedElement = currentWrappers.reduceRight((acc, wrapper) => {
        const CurrentGuard = wrapper.Guard || Fragment;
        const CurrentLayout = wrapper.Layout || Fragment;
        return (
          <CurrentGuard>
            <CurrentLayout>{acc}</CurrentLayout>
          </CurrentGuard>
        );
      }, baseElement);

      entries.push({ path, element: wrappedElement });
    });
  });

  return entries;
};

export const renderRoutes = (routes = []) => (
  <Suspense
    fallback={
      <LoadingScreen
        message={"routes"}
        withBackdrop
        suppressWhenGlobalLoading={false}
      />
    }
  >
    <Routes>
      {buildRouteEntries(routes).map((entry, index) => {
        const routePath = entry.path || "*";
        return (
          <Route
            key={`${index}-${routePath}`}
            path={routePath}
            element={entry.element}
          />
        );
      })}
    </Routes>
  </Suspense>
);

const routes = [
  {
    path: "/404",
    component: lazy(() => import("src/views/errors/NotFoundView")),
  },
  {
    guard: GuestGuard,
    path: ["/:customerName/login", "/login"],
    component: lazy(() => import("src/views/auth/LoginView/index")),
  },
  {
    path: "/email",
    layout: DashboardLayout,
    component: lazy(() => import("src/views/mail/MailView/index")),
  },
  {
    path: "/certificate/validation/:id/",
    component: lazy(() => import("src/views/arkadu/certificate/index")),
  },
  {
    path: ["/establecimiento/:uid", "/establishment/:uid"],
    component: lazy(
      () => import("src/views/arkadu/taxes/license/qr/LicensePublicSummary"),
    ),
  },
  {
    path: [
      "/certificate/vehicle/taxes/",
      "/certificate/vehicle/taxes/:serialID",
    ],
    component: lazy(
      () => import("src/views/arkadu/certificate/VehicleCertificates"),
    ),
  },
  {
    path: "/app",
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: app_routes,
  },
  {
    path: "/subscriptions",
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: stripe_routes,
  },
  {
    path: "*",
    routes: [
      {
        path: "/:customerName/monitor/*",
        layout: MainLayout,
        guard: AuthGuard,
        routes: [
          {
            path: "/:customerName/monitor/appointments/",
            component: lazy(
              () =>
                import("src/views/arkadu/corporate/appointments/AppointmentsCallMonitor"),
            ),
          },
        ],
      },
      {
        layout: MainLayout,
        guard: AuthGuard,
        path: [
          "/:customerName/AdminDashboard/:tabName",
          "/:customerName/AdminDashboard",
        ],
        component: lazy(
          () => import("src/views/arkadu/dashboard/AdminDashBoard"),
        ),
      },
      {
        layout: BlogLayout,
        path: ["/legal/:id", "/legal/"],
        component: lazy(() => import("src/views/arkadu/blog/LegalPage")),
      },
      {
        layout: MainLayout,
        path: [
          "/:customerName/eventads/public/",
          "/:customerName/eventads/public",
        ],
        component: EventadsPublicPortal,
      },
      {
        layout: BlogLayout,
        path: "/:customerName/blog/:id",
        component: lazy(() => import("src/views/arkadu/blog/BlogPostDetail")),
      },
      {
        layout: BlogLayout,
        path: "/post/:id",
        component: lazy(() => import("src/views/arkadu/blog/BlogPostDetail")),
      },

      {
        path: "/:customerName/",
        layout: DashboardLayout,
        routes: [
          {
            guard: AuthGuard,
            path: [
              "/:customerName/cms/post/new/",
              "/:customerName/cms/post/:id/",
            ],
            component: lazy(() => import("src/views/arkadu/blog/BlogPostEdit")),
          },
          {
            guard: AuthGuard,

            path: ["/:customerName/cms/:tabName/", "/:customerName/cms/"],
            component: lazy(() => import("src/views/arkadu/blog/BlogMain")),
          },
          // {
          //   path: '/Guaicaipuro',
          //   component: lazy(() => import('src/views/arkadu/Maintenance'))
          // },
          // {
          //   path: "/:customerName/geo/GMaps/",
          //   guard: AuthGuard,
          //   component: lazy(() =>
          //     import("src/views/arkadu/geo/GoogleMapsAddress")
          //   ),
          // },
          {
            guard: AuthGuard,
            path: "/:customerName/audit/*",
            routes: audit_routes,
          },
          {
            guard: AuthGuard,
            path: "/:customerName/approval/*",
            routes: approval_routes,
          },
          {
            path: "/:customerName/login",
            guard: GuestGuard,
            component: lazy(() => import("src/views/auth/LoginView/index")),
          },
          {
            path: "/:customerName/dinvoice/*",
            guard: AuthGuard,
            routes: dinvoice_routes,
          },
          {
            path: [
              "/:customerName/documents/:tabName",
              "/:customerName/documents/",
            ],
            guard: AuthGuard,
            component: lazy(() => import("src/views/arkadu/documents/index")),
          },
          {
            path: "/:customerName/bank/",
            layout: BankLayout,
            guard: AuthGuard,
            routes: [
              {
                path: "/:customerName/bank/transfer/result/:id/",
                component: lazy(
                  () => import("src/views/arkadu/bank/TransferResult"),
                ),
              },
              {
                path: "/:customerName/bank/transfer/same/",
                component: lazy(
                  () => import("src/views/arkadu/bank/SameUserTransfer"),
                ),
              },
              {
                path: "/:customerName/bank/:serviceId/products/",
                component: lazy(
                  () => import("src/views/arkadu/bank/BankProducts"),
                ),
              },
              {
                path: [
                  "/:customerName/bank/admin/:serviceId/:environmentId/",
                  "/:customerName/bank/admin/:serviceId/",
                ],
                component: lazy(
                  () => import("src/views/arkadu/bank/BankServiceAdmin"),
                ),
              },
              {
                path: "/:customerName/bank/",
                component: lazy(
                  () => import("src/views/arkadu/bank/BankIndex"),
                ),
              },
            ],
          },
          {
            path: "/:customerName/userRegistration/",
            guard: AuthGuard,
            component: lazy(
              () => import("src/views/arkadu/user/SimpleRegistrationForm"),
            ),
          },
          {
            path: "/:customerName/serviceRegistration/",
            guard: AuthGuard,
            component: lazy(
              () => import("src/views/arkadu/user/ServiceRegistrationForm"),
            ),
          },

          {
            path: "/:customerName/taxes/*",
            layout: TaxesLayout,
            guard: AuthGuard,
            routes: taxes_routes,
          },
          {
            path: "/:customerName/infeco/",
            layout: InformalEconomyLayout,
            guard: AuthGuard,
            routes: informal_economy_routes,
          },
          {
            path: "/:customerName/service/",
            guard: AuthGuard,
            layout: ServicesLayout,
            routes: service_routes,
          },
          {
            path: "/:customerName/system/dev/",
            guard: AuthGuard,
            layout: DevLayout,
            routes: dev_routes,
          },
          {
            path: "/:customerName/notify/",
            guard: AuthGuard,
            // layout: UrbanCleaningLayout,
            routes: notify_routes,
          },
          {
            path: "/:customerName/global/",
            layout: GlobalLayout,
            guard: AuthGuard,
            routes: [
              {
                path: ["/:customerName/global/"],
                component: lazy(
                  () => import("src/views/arkadu/global/GlobalIndex"),
                ),
              },
            ],
          },
          {
            path: "/:customerName/customer/",
            layout: CustomerLayout,
            guard: AuthGuard,
            routes: [
              {
                path: ["/:customerName/customer/executive-report/"],
                component: lazy(
                  () =>
                    import("src/views/arkadu/customer/CustomerExecutiveReportView"),
                ),
              },
              {
                path: ["/:customerName/customer/report-monitor/"],
                component: lazy(
                  () =>
                    import("src/views/arkadu/customer/CustomerReportMonitorView"),
                ),
              },
              {
                path: ["/:customerName/customer/"],
                component: lazy(
                  () => import("src/views/arkadu/customer/CustomerIndex"),
                ),
              },
            ],
          },
          {
            path: "/:customerName/erp",
            guard: AuthGuard,
            routes: erp_routes,
          },
          {
            path: "/:customerName/socialresp",
            guard: AuthGuard,
            routes: socialresp_routes,
          },
          {
            path: "/:customerName/certificate",
            guard: AuthGuard,
            routes: certificate_admin_routes,
          },
          {
            path: "/:customerName/signature",
            guard: AuthGuard,
            routes: signature_routes,
          },
          {
            path: "/:customerName/eventads",
            guard: AuthGuard,
            layout: EventadsLayout,
            routes: eventads_routes,
          },
          {
            path: "/:customerName/requests/",
            guard: AuthGuard,
            routes: requests_routes,
          },
          {
            path: "/:customerName/corporate/",
            guard: AuthGuard,
            layout: CorporateLayout,
            routes: corporate_routes,
          },
          {
            path: "/:customerName/config",
            guard: AuthGuard,
            component: lazy(
              () =>
                import("src/views/arkadu/customer/forms/CustomerSettingsForm"),
            ),
          },

          {
            guard: AuthGuard,
            path: "/:customerName/",
            component: lazy(
              () => import("src/views/arkadu/module/CustomerIndex"),
            ),
          },

          {
            guard: AuthGuard,
            path: "/:customerName/posts/new/",
            component: lazy(
              () => import("src/views/arkadu/module/Timeline/NewPost"),
            ),
          },

          {
            guard: AuthGuard,
            path: "/:customerName/taxes/declaration/:declarationId/",
            component: lazy(
              () => import("src/views/arkadu/taxes/DeclarationDetail"),
            ),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/taxes/license/:licenseId",
            component: lazy(
              () =>
                import("src/views/arkadu/taxes/license/parts/LicenseDetailView"),
            ),
          },

          // {
          //   exact: true,
          //   guard: AuthGuard,
          //   path: [
          //     '/:customerName/taxes/',
          //     '/:customerName/taxes/:tabName/:periodId',
          //     // '/:customerName/taxes/periods/',
          //     // '/:customerName/taxes/reports/',
          //     // '/:customerName/taxes/classifier/',
          //     '/:customerName/taxes/:tabName/'
          //   ],
          //   component: lazy(() =>
          //     import('src/views/arkadu/taxes/TaxesDashboard')
          //   )
          // },
          // {
          //   exact: true,
          //   guard: AuthGuard,
          //   path: [
          //     '/:customerName/taxes/license/:licenseId/declaration/:declarationId',
          //     '/:customerName/taxes/declaration/:id/certificate/',
          //     '/:customerName/taxes/declaration/:id/',
          //     '/:customerName/taxes/license/:id',
          //     '/:customerName/taxes/:currentTab/:id',
          //     '/:customerName/taxes/:currentTab/',
          //     '/:customerName/taxes/'
          //   ],
          //   component: lazy(() => import('src/views/arkadu/taxes/index'))
          // },

          {
            guard: AuthGuard,
            path: "/:customerName/taxes/declaration/:id/certificate/",
            component: lazy(
              () =>
                import("src/views/arkadu/taxes/CertificateDetailsView/index"),
            ),
          },
          {
            path: "/:customerName/accounts",
            guard: AuthGuard,
            layout: ShopLayout,
            routes: account_routes,
          },
          {
            path: "/:customerName/poll",
            guard: AuthGuard,
            layout: PollLayout,
            routes: poll_routes,
          },
          {
            path: "/:customerName/cadaster/",
            guard: AuthGuard,
            layout: CadasterLayout,
            routes: cadaster_routes,
          },
          {
            path: "/:customerName/urbin/",
            layout: UrbinLayout,
            guard: AuthGuard,
            routes: urbin_routes,
          },
          {
            path: "/:customerName/environment/",
            guard: AuthGuard,
            layout: EnvironmentLayout,
            routes: environment_routes,
          },
          {
            path: "/:customerName/classroom/",
            guard: AuthGuard,
            routes: classroom_routes,
          },
          {
            path: "/:customerName/fire-department/",
            guard: AuthGuard,
            layout: FireDepartmentLayout,
            routes: fire_department_routes,
          },
          {
            guard: AuthGuard,
            layout: StampLayout,
            path: ["/:customerName/stamp/"],
            routes: stamp_routes,
          },
          {
            path: "/:customerName/suppliers/",
            guard: AuthGuard,
            layout: SuppliersLayout,
            routes: suppliers_routes,
          },
          {
            path: "/:customerName/planning/",
            guard: AuthGuard,
            layout: PlanningLayout,
            routes: planning_routes,
          },
          {
            guard: AuthGuard,
            path: "/:customerName/support/ticket/new/",
            component: lazy(
              () => import("src/views/arkadu/support/NewTicketForm"),
            ),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/support/ticket/detail/:id",
            component: lazy(
              () => import("src/views/arkadu/support/TicketDetail"),
            ),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/support/",
            component: lazy(
              () => import("src/views/arkadu/support/SupportIndex"),
            ),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/police/",
            routes: police_routes,
          },

          {
            guard: AuthGuard,
            path: "/:customerName/advertise/",
            component: lazy(() => import("src/views/arkadu/advertise/index")),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/civil/",
            component: lazy(() => import("src/views/arkadu/civil/index")),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/urban/",
            component: lazy(() => import("src/views/arkadu/urban/index")),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/corporateAdmin/users/",
            component: lazy(
              () => import("src/views/arkadu/admin/corporate/parts/StaffAdmin"),
            ),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/corporateAdmin/",
            component: lazy(
              () =>
                import("src/views/arkadu/admin/corporate/CorporateIndexView"),
            ),
          },
          // {
          //   exact: true,
          //   guard: AuthGuard,
          //   layout: VehicleMotorLayout,
          //   path: "/:customerName/motor/fix/",
          //   component: lazy(() =>
          //     import("src/views/arkadu/motor/FixMotorIndex")
          //   ),
          // },
          {
            guard: AuthGuard,
            layout: VehicleMotorLayout,
            path: "/:customerName/motor/stickers/",
            component: lazy(
              () => import("src/views/arkadu/motor/MotorSticker"),
            ),
          },
          {
            guard: AuthGuard,
            layout: VehicleMotorLayout,
            path: "/:customerName/motor/passenger-lists/",
            component: lazy(
              () => import("src/views/arkadu/motor/MotorPassengerList"),
            ),
          },
          {
            guard: AuthGuard,
            layout: VehicleMotorLayout,
            path: "/:customerName/motor/passenger-sales/",
            component: lazy(
              () => import("src/views/arkadu/motor/MotorPassengerSales"),
            ),
          },
          {
            guard: AuthGuard,
            layout: VehicleMotorLayout,
            path: [
              "/:customerName/motor/inscription",
              "/:customerName/motor/newVehicle",

              "/:customerName/motor/:tabName",
              "/:customerName/motor/",
            ],
            component: lazy(() => import("src/views/arkadu/motor/index")),
          },
          {
            guard: AuthGuard,
            layout: VehicleMotorLayout,
            path: [
              "/:customerName/motor/vehicle/statement/:vehicleId",
              "/:customerName/motor/vehicle/:inscriptionId",
            ],
            component: lazy(
              () => import("src/views/arkadu/motor/VehicleMotorDetail"),
            ),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/translate/:id",
            component: lazy(
              () => import("src/views/arkadu/trans/TransSetView"),
            ),
          },
          {
            guard: AuthGuard,
            path: "/:customerName/translate/",
            component: lazy(() => import("src/views/arkadu/trans/index")),
          },

          {
            path: "/:customerName/admin/*",
            routes: [
              {
                path: "/:customerName/admin/taxes/declaration/:id",
                guard: AuthGuard,
                component: lazy(
                  () => import("src/views/arkadu/taxes/DeclarationDetail"),
                ),
              },

              {
                path: "/:customerName/admin/taxes/period/add/",
                guard: AuthGuard,
                component: lazy(
                  () => import("src/views/arkadu/taxes/admin/period/AddPeriod"),
                ),
              },
              {
                path: "/:customerName/admin/taxes/period/:id/addFee/",
                guard: AuthGuard,
                component: lazy(
                  () =>
                    import("src/views/arkadu/taxes/admin/period/AddPeriodFee"),
                ),
              },
              {
                path: "/:customerName/admin/taxes/period/:id/",
                guard: AuthGuard,
                component: lazy(
                  () =>
                    import("src/views/arkadu/taxes/admin/period/PeriodView"),
                ),
              },
              {
                path: "/:customerName/admin/taxes/:tabName",
                guard: AuthGuard,
                component: lazy(
                  () => import("src/views/arkadu/taxes/admin/index"),
                ),
              },
              {
                path: "/:customerName/admin/taxes/",
                guard: AuthGuard,
                component: lazy(
                  () => import("src/views/arkadu/taxes/admin/index"),
                ),
              },

              {
                guard: AuthGuard,
                path: [
                  "/:customerName/admin/support/:userId/ticket/:ticketId",
                  "/:customerName/admin/support/:userId",
                  "/:customerName/admin/support/",
                ],
                component: lazy(
                  () => import("src/views/arkadu/support/SupportIndex"),
                ),
              },
              {
                path: "/:customerName/admin/accounts/",
                guard: AuthGuard,
                component: lazy(
                  () => import("src/views/arkadu/accounts/AddFundAdmin"),
                ),
              },
              {
                path: "/:customerName/admin/user/registration/",
                guard: AuthGuard,
                layout: UserLayout,
                component: lazy(
                  () => import("src/views/arkadu/user/SimpleRegistrationForm"),
                ),
              },
              {
                path: "/:customerName/admin/user/service-registration/",
                guard: AuthGuard,
                layout: UserLayout,
                component: lazy(
                  () => import("src/views/arkadu/user/ServiceRegistrationForm"),
                ),
              },
              {
                path: "/:customerName/admin/user/:userId/deposit/",
                guard: AuthGuard,
                component: lazy(
                  () =>
                    import("src/views/arkadu/user/admin/components/AddFundAdminStepper"),
                ),
              },
              {
                path: ["/:customerName/admin/user/:userId/notification/"],
                guard: AuthGuard,
                component: lazy(
                  () => import("src/views/arkadu/notify/NewNotifyMessage"),
                ),
              },

              {
                path: [
                  "/:customerName/admin/user/:userId/:tabName",
                  "/:customerName/admin/user/:userId",
                ],
                guard: AuthGuard,
                component: lazy(
                  () => import("src/views/arkadu/user/admin/UserAdmin"),
                ),
              },
              {
                path: "/:customerName/admin/cadaster/",
                guard: AuthGuard,
                component: lazy(
                  () => import("src/views/arkadu/cadaster/admin/index"),
                ),
              },

              {
                path: "/:customerName/admin/",
                guard: AuthGuard,
                component: lazy(
                  () =>
                    import("src/views/reports/DashboardAlternativeView/index"),
                ),
              },
            ],
          },
          {
            layout: BlogLayout,
            path: "/",
            component: BlogIndex,
          },
          {
            component: () => <Navigate to="/404" replace />,
          },
        ],
      },

      {
        component: () => <Navigate to="/404" replace />,
      },
    ],
  },
];

export default routes;
